import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { ArrowRight } from "lucide-react";
import { Img, SIZES } from "@/components/Img";
import { type Locale } from "@/i18n/config";
import { pathFor } from "@/i18n/slugMap";
import { pageHead } from "@/i18n/head";
import { samarbejdspartnereStrings } from "@/i18n/content/samarbejdspartnere";
import abc from "@/assets/partners/abc.png.asset.json";
import alba from "@/assets/partners/alba.png.asset.json";
import cowi from "@/assets/partners/cowi.png.asset.json";
import medicWiotech from "@/assets/partners/medic-wiotech.jpg.asset.json";
import digitalRealty from "@/assets/partners/digital-realty.png.asset.json";
import interxion from "@/assets/partners/interxion.png.asset.json";
import noaAd from "@/assets/partners/noa-ad-white.png.asset.json";
import proAccount from "@/assets/partners/proaccount.png.asset.json";
import resonans from "@/assets/partners/resonans.png.asset.json";
import siemens from "@/assets/partners/siemens.svg.asset.json";
import sweco from "@/assets/partners/sweco.png.asset.json";

/** <head> for the business-partners page: pageHead with breadcrumb (no Service schema). */
export function samarbejdspartnereHead(locale: Locale) {
  const s = samarbejdspartnereStrings[locale];
  return pageHead("samarbejdspartnere", locale, s.meta, {
    breadcrumbName: s.hero.title,
  });
}

// w/h: only needed for SVG logos — they are not in imageManifest, so <Img>
// can't derive intrinsic dimensions (raster logos get them from the manifest).
const partners: { name: string; img: string; whiteBg?: boolean; w?: number; h?: number }[] = [
  { name: "Novo Nordisk", img: "/wp-content/uploads/Novo-Nordisk_300x200.png" },
  { name: "NNE", img: "/wp-content/uploads/NNE_300x200.png" },
  { name: "Novonesis", img: "/wp-content/uploads/Novonesis_300x200.jpg" },
  { name: "Ørsted", img: "/wp-content/uploads/Orsted_300x200.png" },
  { name: "Digital Realty", img: digitalRealty.url, whiteBg: true },
  { name: "Lundbeck", img: "/wp-content/uploads/Lundbeck_300x200.png" },
  { name: "Nordika", img: "/wp-content/uploads/Nordika_300x200.png" },
  { name: "CPH", img: "/wp-content/uploads/CPH_300x200.png" },
  { name: "Pronova", img: "/wp-content/uploads/Pronova_300x200.png" },
  { name: "Carlsberg", img: "/wp-content/uploads/Carlsberg_300x200.png" },
  { name: "Chr. Hansen", img: "/wp-content/uploads/Chr-Hansen_300x200.png" },
  { name: "Pfizer", img: "/wp-content/uploads/Pfizer2_300x200.png" },
  { name: "Nordic Harvest", img: "/wp-content/uploads/Nordic-Harvest_300x200.png" },
  { name: "Labflex", img: "/wp-content/uploads/Labflex_300x200.png" },
  { name: "Ferring", img: "/wp-content/uploads/Ferring_300x200.png" },
  { name: "Arla", img: "/wp-content/uploads/Arla_300x200.png" },
  { name: "Amgen", img: "/wp-content/uploads/Amgen_300x200.png" },
  { name: "Bavarian Nordic", img: "/wp-content/uploads/Bavarian-Nordic_300x200.png" },
  { name: "Ungdomsøen", img: "/wp-content/uploads/Ungdomsoen_300x200.png" },
  { name: "GEA", img: "/wp-content/uploads/GEA_300x200.png" },
  { name: "Niro", img: "/wp-content/uploads/Niro_300x200.png" },
  { name: "CMC", img: "/wp-content/uploads/CMC_300x200.png" },
  { name: "Vejdirektoratet", img: "/wp-content/uploads/Vejdirektoratet_300x200.png" },
  { name: "BWSC", img: "/wp-content/uploads/BWSC_300x200.png" },
  { name: "Rockwool", img: "/wp-content/uploads/Rockwool_300x200.png" },
  { name: "Kalundborg Havn", img: "/wp-content/uploads/KalundborgHavn_300x200.png" },
  { name: "Bedrocan", img: "/wp-content/uploads/Bedrocan_300x200.png" },
  { name: "Novartis", img: "/wp-content/uploads/Novartis_300x200.png" },
  { name: "Allgemeine Bau-Chemie", img: abc.url, whiteBg: true },
  { name: "Alba Project Group", img: alba.url, whiteBg: true },
  { name: "COWI", img: cowi.url, whiteBg: true },
  { name: "Medic Wiotech", img: medicWiotech.url, whiteBg: true },
  { name: "NOA.AD", img: noaAd.url, whiteBg: true },
  { name: "ProAccount", img: proAccount.url, whiteBg: true },
  { name: "Resonans", img: resonans.url, whiteBg: true },
  { name: "Siemens", img: siemens.url, whiteBg: true, w: 1000, h: 159 },
  { name: "Sweco", img: sweco.url, whiteBg: true },
  { name: "Interxion", img: interxion.url, whiteBg: true },
];

export function SamarbejdspartnerePage({ locale }: { locale: Locale }) {
  const t = samarbejdspartnereStrings[locale];

  return (
    <SiteLayout>
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <p className="text-sm uppercase tracking-widest text-foreground/70">{t.hero.eyebrow}</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight">{t.hero.title}</h1>
          <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.hero.body}</p>
        </div>
      </section>

      <section className="pb-12">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.list.heading}</h2>
          <p className="mt-6 text-foreground/85 leading-relaxed">{t.list.body}</p>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {partners.map((p) => (
              <div
                key={p.name}
                className="rounded-xl p-6 flex items-center justify-center aspect-[3/2] overflow-hidden bg-white"
              >
                <Img src={p.img} alt={p.name} width={p.w} height={p.h} className="w-full h-auto object-contain" sizes={SIZES.logo} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-secondary py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <p className="text-2xl md:text-3xl font-medium italic leading-snug">
           {t.quote.text}
          </p>
          <p className="mt-8 font-semibold">{t.quote.name}</p>
          <p className="text-foreground/80 text-sm">{t.quote.role}</p>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-3xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.cta.heading}</h2>
          <Link
            to={pathFor("kontakt", locale)}
            className="mt-8 btn-cta"
          >
            {t.cta.button} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
